import { Injectable } from '@angular/core';
import { LocalStoreService } from '../local-store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { User } from '../../../state/models/user.model';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { PersonEntityService } from '../../../state/entity-services/person-entity.service';
import { Person } from '../../../state/models/person';
import { AuthResponse, Token } from '../../../state/models/token';
import { TokenEntityService } from '../../../state/entity-services/token-entity.service';
import * as Sentry from '@sentry/angular';
import { EntityManagementService } from '../../../state/entity-management/services/base/entity-management.service';
import * as amplitude from '@amplitude/analytics-browser';
import { SubscriptionEntityService } from '../../../state/entity-services/subscription-entity.service';
import { FeatureFlagService } from '../feature-flag.service';
import { OrganizationService } from '../organization.service';

@Injectable({
  providedIn: 'root',
})
export class JwtAuthService {
  token: string | null = null;
  isAuthenticated: boolean = false;
  user: User | undefined = undefined;
  user$ = new BehaviorSubject<User | undefined>(this.user);
  signingIn: boolean = false;
  return: string = '';
  JWT_TOKEN = 'JWT_TOKEN';
  APP_USER = 'EGRET_USER';
  JWT_REFRESH_TOKEN = 'JWT_REFRESH_TOKEN';
  amplitude: any = amplitude;
  refreshTokenInProgress: boolean = false;


  constructor(
    private organizationService: OrganizationService,
    private featureFlagService: FeatureFlagService,
    private subscriptionEntityService: SubscriptionEntityService,
    private entityManagementService: EntityManagementService,
    private tokenEntityService: TokenEntityService,
    private personEntityService: PersonEntityService,
    private ls: LocalStoreService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
  }

  get isLoggedIn(): boolean {
    return !!this.getJwtToken();
  }

  public signInCode(code: string, document?: string): Observable<any> {
    this.signingIn = true;
    return this.authCode(code, document).pipe(
      tap((res: AuthResponse) => {
        this.setUserAndToken(res);
      }),
      mergeMap((res: any) =>
        this.getSelf().pipe(
          map(user => Object.assign({}, { token: res, user })),
        ),
      ),
      map((res: any) => {
        this.setUserAndToken(res.token, res.user, !!res);

        return res;
      }),
      switchMap((res) => {
        return this.subscriptionEntityService.getWithQuery({
          personIds: res.user.id,
          active: true
        });
      }),
      switchMap((subscriptions) => {
        const [subscription] = subscriptions || [];
        this.signingIn = false;
        this.organizationService.setOrganization(subscription?.organization);
        return this.featureFlagService.getFeatureFlagSearchesByOrgId(subscription?.organization?.id);
      }),
      catchError(error => {
        console.log(error);
        return throwError(error);
      }),
    );
  }

  /*
    checkTokenIsValid is called inside constructor of
    shared/components/layouts/admin-layout/admin-layout.component.ts
  */
  public checkTokenIsValid() {
    return this.getSelf().pipe(
      map((profile: User) => {
        this.setUserAndToken(this.getJwtToken(), profile, true);
        return profile;
      }),
      catchError(error => {
        this.logout();
        return of(error);
      }),
    );
  }

  public logout(redirect?: boolean) {
    this.entityManagementService.clearAllCache();
    this.setUserAndToken(null, null, false);
    this.amplitude.setUserId(null); // Remove o User ID
    this.amplitude.setSessionId(Date.now()); // Força uma nova sessão
    this.amplitude.reset();
    this.ls.removeItem('formContext');
    this.ls.removeItem('formContextPrev');
    this.ls.removeItem('lastPosition');
    localStorage.clear();
    if (redirect) {
      this.router.navigate(['/sessions/logout']);
    }
  }

  getJwtToken() {
    return this.ls.getItem(this.JWT_TOKEN);
  }

  getUser(): Person {
    return this.ls.getItem(this.APP_USER) as Person;
  }

  setUserAndToken(tokens: AuthResponse, user?: User, isAuthenticated = false) {
    this.isAuthenticated = isAuthenticated;
    const { accessToken, refreshToken } = tokens || {};
    this.token = accessToken;
    if (user || user === null) {
      this.user = user;
      this.user$.next(user);
      this.ls.setItem(this.APP_USER, user);
    }
    this.ls.setItem(this.JWT_TOKEN, accessToken);
    this.ls.setItem(this.JWT_REFRESH_TOKEN, refreshToken);
    this.setUserSentry(true);
  }

  setUserSentry(force?: boolean): void {
    const { id, email, phone, document } = this.getUser() || {};
    if (!document && force === undefined) {
      return;
    }

    Sentry.setUser(
      {
        id,
      }
    );
  }

  private authCode(code: string, document?: string): Observable<Token> {
    console.log('authCode', code, document);
    return this.tokenEntityService.getToken(code, document);
  }

  private getSelf(): Observable<User> {
    return this.personEntityService.getSelf();
  }

  refreshToken() {
    this.refreshTokenInProgress = true;
    const body = {
      grantType: 'REFRESH_TOKEN',
      refreshToken: this.getJwtRefreshToken(),
    };
    return this.signIn(body).pipe(
      tap((res: any) => {
        this.refreshTokenInProgress = false;
      }),
      catchError(error => {
        this.refreshTokenInProgress = false;
        return throwError(error);
      }),
    );
  }

  getJwtRefreshToken() {
    return this.ls.getItem(this.JWT_REFRESH_TOKEN);
  }


  signIn(body: any) {
    this.signingIn = true;
    return this.tokenEntityService.getTokenGeneral(body).pipe(
      tap((res) => {
        const { accessToken, refreshToken } = res as AuthResponse;
        this.setUserAndToken({ accessToken, refreshToken });
      }),
      mergeMap((res: any) =>
        this.getSelf().pipe(
          map(user => Object.assign({}, { token: res, user })),
        ),
      ),
      map((res: any) => {
        this.setUserAndToken(res.token, res.user, !!res);
        this.signingIn = false;
        return res;
      }),
      catchError(error => {
        return throwError(error);
      }),
    );
  }
}
